import React, { useCallback, useId } from 'react';
import { FieldLabel } from '@puckeditor/core';
import type { CustomField } from '@puckeditor/core';

/**
 * Value shape for the combined alt-text/decorative control.
 *
 * Modelled as a single object so the two travel together and can never disagree (e.g. a
 * decorative image that also carries stale alt text).
 *
 * `alt` is a kept key on `ImageProps` — the block's original prop was a top-level `string`, so
 * `image.tsx`'s `alt` type widens to `string | AltTextValue`. {@link resolveAltText} is the
 * single normalisation point used by both this widget and `image.tsx`'s `render`.
 */
export type AltTextValue = {
  alt: string;
  decorative: boolean;
};

export const DEFAULT_ALT_TEXT_VALUE: AltTextValue = { alt: '', decorative: false };

/**
 * Normalises a saved `alt` value to an `AltTextValue`, tolerating the legacy shape.
 *
 * `alt` kept its key across the refactor: a page saved before this control existed holds a
 * plain string (`'a photo'`). Puck does not inject `defaultProps` in the published `Render`
 * path, so that legacy string genuinely reaches both this widget (in the editor) and
 * `image.tsx`'s `render` (in production) — this is the one place both normalise it, so a
 * legacy string and the new `{ alt, decorative }` object always resolve the same way.
 */
export function resolveAltText(value: unknown): AltTextValue {
  if (typeof value === 'string') return { alt: value, decorative: false };

  // Saved page JSON is unvalidated: `null`, arrays, numbers, and partial/mistyped objects (e.g.
  // `{ decorative: true }` with no `alt` key, or `{ alt: null }`) have all been observed in the
  // wild. None of those are safe to pass through as-is -- the published `Render` path has no
  // error boundary, so a single malformed block would white-screen the whole page the moment a
  // consumer touches `.alt.trim()` or `.decorative`. Coerce field-by-field instead of trusting
  // the shape.
  if (typeof value !== 'object' || value === null) return DEFAULT_ALT_TEXT_VALUE;

  const candidate = value as Partial<Record<keyof AltTextValue, unknown>>;
  const alt = typeof candidate.alt === 'string' ? candidate.alt : '';
  const decorative = candidate.decorative === true;

  return { alt, decorative };
}

type AltTextFieldProps = {
  field: { label?: string };
  name: string;
  value: unknown;
  onChange: (value: AltTextValue) => void;
  readOnly?: boolean;
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '6px 8px',
  border: '1px solid #d0d4d8',
  borderRadius: 4,
  fontSize: 14,
  boxSizing: 'border-box',
};

const checkboxRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  marginTop: 8,
};

// Matches `select-field.tsx`'s `hintStyle` (fontSize 12, muted grey) — inline caption text next
// to the checkbox, not a row-prefix label. Keeps the checkbox label and warning text at the same
// visual weight so they read as one coherent block under the alt input.
const checkboxLabelStyle: React.CSSProperties = {
  fontSize: 12,
  color: '#646d72',
};

const warningStyle: React.CSSProperties = {
  marginTop: 6,
  fontSize: 12,
  color: '#a15c00',
};

/**
 * Puck custom field combining alt text with a "This image is decorative" checkbox.
 *
 * Ticking decorative disables (and visually de-emphasises) the alt input — decorative images
 * render `alt=""` regardless of whatever text was previously entered, so the disabled input
 * communicates that the text is currently inert without discarding it (unticking restores it).
 *
 * When alt is empty and the image is not marked decorative, a non-blocking inline warning
 * explains the accessibility gap. It is informational only and must never block publish — Puck's
 * own validation is not invoked here, so there is nothing to block.
 */
export function AltTextField({ field, name, value, onChange, readOnly }: AltTextFieldProps): JSX.Element {
  const current = resolveAltText(value);
  const warningId = useId();

  const handleAltChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (readOnly || current.decorative) return;
      onChange({ ...current, alt: e.currentTarget.value });
    },
    [current, onChange, readOnly]
  );

  const handleDecorativeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (readOnly) return;
      onChange({ ...current, decorative: e.currentTarget.checked });
    },
    [current, onChange, readOnly]
  );

  const showWarning = !current.decorative && current.alt.trim() === '';

  return (
    <FieldLabel label={field.label ?? name} readOnly={readOnly}>
      <input
        type='text'
        value={current.alt}
        onChange={handleAltChange}
        disabled={(readOnly ?? false) || current.decorative}
        placeholder='Describe this image for screen readers'
        aria-label={`${field.label ?? name} alt text`}
        aria-describedby={showWarning ? warningId : undefined}
        data-testid={`alt-text-input-${name}`}
        style={{ ...inputStyle, opacity: current.decorative ? 0.5 : 1 }}
      />

      <label style={checkboxRowStyle} htmlFor={`alt-text-decorative-${name}`}>
        <input
          id={`alt-text-decorative-${name}`}
          type='checkbox'
          checked={current.decorative}
          onChange={handleDecorativeChange}
          disabled={readOnly}
          data-testid={`alt-text-decorative-${name}`}
        />
        <span style={checkboxLabelStyle}>This image is decorative</span>
      </label>

      {showWarning && (
        <p id={warningId} data-testid={`alt-text-warning-${name}`} style={warningStyle}>
          Screen readers will skip this image. Add alt text, or mark it decorative.
        </p>
      )}
    </FieldLabel>
  );
}

/**
 * Builds a complete Puck `custom` alt-text field, including the `ai.schema` the AI plugin
 * requires for every `custom` field. Mirrors `selectField()`'s guard: a bare
 * `{ type: 'custom', render: AltTextField }` works in the editor UI but 400s the entire AI chat
 * request the moment the AI plugin reaches it, so the schema is baked in here rather than left to
 * each call site to remember.
 */
export function altTextField(label: string, aiInstructions?: string): CustomField<AltTextValue> {
  return {
    type: 'custom',
    label,
    render: AltTextField,
    ai: {
      schema: {
        type: 'object',
        required: ['alt', 'decorative'],
        properties: {
          alt: {
            type: 'string',
            description: 'Descriptive alt text for screen readers. Ignored when decorative is true.',
          },
          decorative: {
            type: 'boolean',
            description: "True if this image is purely decorative and should render alt=''.",
          },
        },
      },
      // Alt text is a short, single-field value with no useful intermediate state -- streaming it
      // token-by-token would just flash partial nonsense in the input. Mirrors `src`/`linkHref`.
      stream: false,
      ...(aiInstructions ? { instructions: aiInstructions } : {}),
    },
  };
}
